import { useEffect, useState } from 'react'
import { Plus, Trash2, Shield, ChevronDown, ChevronRight, Users, Copy } from 'lucide-react'
import { dialog } from '../../../services/dialog'
import api, { adminApi } from '../../../services/api'
import { useToastStore } from '../../../stores/toastStore'
import { useAuthStore } from '../../../stores/authStore'
import {
  PERMISSION_GROUPS,
  ALL_PERMISSIONS,
  baselinePermissionsBits,
  hasBit,
  toggleBit,
} from './permissionCatalog'

interface RoleRow {
  id: string
  name: string
  color: string
  permissions: string
  serverMemberRoles?: Array<{ id: string }>
}

const PRESET_COLORS = [
  '#99aab5', '#3b82f6', '#10b981', '#f59e0b',
  '#ef4444', '#a855f7', '#ec4899', '#14b8a6',
]

/**
 * Rewritten Roles tab.
 *
 * Changes vs the inline flat-grid version:
 *   - Permissions grouped by module (Channels / Voice & Video /
 *     Moderation / Server management / Administrator) so a 12+ list
 *     stops reading as a wall.
 *   - "New role" defaults to the baseline permission set
 *     (View Channel, Send Messages, Connect, Speak, Video, Screen Share)
 *     so the common case lands ready-to-assign.
 *   - Each role shows a member count + a colour picker.
 *   - Groups collapse so the long ones don't eat vertical space.
 */
export function RolesTab({ serverId }: { serverId: string }) {
  const showToast = useToastStore((s) => s.showToast)
  // OSPROD-247: cloning roles across servers is a GLOBAL-admin operation
  // (the source-server picker lists every server via /admin/servers). Only
  // surface the "Clone from server" affordance to global admins.
  const isGlobalAdmin = useAuthStore((s) => s.user?.isAdmin ?? false)
  const [roles, setRoles] = useState<RoleRow[]>([])
  const [loading, setLoading] = useState(true)
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())
  const [cloneOpen, setCloneOpen] = useState(false)
  const [cloneServers, setCloneServers] = useState<Array<{ id: string; name: string }>>([])
  const [cloneBusy, setCloneBusy] = useState(false)

  const fetchRoles = async () => {
    setLoading(true)
    try {
      const res = await api.get<RoleRow[]>(`/servers/${serverId}/roles`)
      setRoles(res.data)
    } catch (e: any) {
      showToast({ id: `roles-fetch-${Date.now()}`, type: 'error', title: 'Failed to load roles', body: e?.response?.data?.error })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchRoles() }, [serverId]) // eslint-disable-line

  const createRole = async () => {
    const name = await dialog.prompt({ title: 'New role', message: 'Role name', placeholder: 'e.g. Moderator', required: true })
    if (!name) return
    try {
      // Server seeds with permissions='0' if we don't pass it. We
      // override with the baseline so a new role lands actually
      // usable instead of forcing the admin to click six boxes.
      await api.post(`/servers/${serverId}/roles`, {
        name,
        permissions: baselinePermissionsBits(),
      })
      fetchRoles()
    } catch (e: any) {
      showToast({ id: `role-create-${Date.now()}`, type: 'error', title: 'Failed to create role', body: e?.response?.data?.error })
    }
  }

  // Open the "Clone from server" picker — lazy-load the server list (all
  // servers, global-admin scoped) minus the current one.
  const openClonePicker = async () => {
    if (cloneOpen) { setCloneOpen(false); return }
    try {
      const res = await adminApi.getServers()
      const list: Array<{ id: string; name: string }> = (res.data as Array<{ id: string; name: string }>)
        .filter((s) => s.id !== serverId)
        .map((s) => ({ id: s.id, name: s.name }))
      setCloneServers(list)
      setCloneOpen(true)
    } catch (e: any) {
      showToast({ id: `clone-list-${Date.now()}`, type: 'error', title: 'Failed to load servers', body: e?.response?.data?.error })
    }
  }

  const cloneFrom = async (sourceId: string, sourceName: string) => {
    if (!(await dialog.confirm({
      title: 'Clone roles',
      message: `Copy all roles from "${sourceName}" into this server? Existing roles with the same name are kept (skipped).`,
      confirmLabel: 'Clone roles',
    }))) return
    setCloneBusy(true)
    try {
      const res = await api.post<{ cloned: number; skipped: number }>(`/servers/${serverId}/roles/clone-from/${sourceId}`)
      setCloneOpen(false)
      showToast({
        id: `clone-ok-${Date.now()}`,
        type: 'success',
        title: 'Roles cloned',
        body: `${res.data.cloned} added${res.data.skipped ? `, ${res.data.skipped} skipped` : ''}.`,
      })
      fetchRoles()
    } catch (e: any) {
      showToast({ id: `clone-err-${Date.now()}`, type: 'error', title: 'Failed to clone roles', body: e?.response?.data?.error })
    } finally {
      setCloneBusy(false)
    }
  }

  const deleteRole = async (id: string, name: string) => {
    if (!(await dialog.confirm({ message: `Delete role "${name}"? Members keep their other roles.`, tone: 'destructive', confirmLabel: 'Delete' }))) return
    try {
      await api.delete(`/servers/${serverId}/roles/${id}`)
      fetchRoles()
    } catch (e: any) {
      showToast({ id: `role-delete-${Date.now()}`, type: 'error', title: 'Failed to delete role', body: e?.response?.data?.error })
    }
  }

  const updateRole = async (id: string, patch: Partial<RoleRow>) => {
    // Optimistic + persist. We don't fetchRoles after — that would
    // collapse all the local UI state (open colour pickers etc).
    setRoles((prev) => prev.map((r) => r.id === id ? { ...r, ...patch } : r))
    try {
      await api.put(`/servers/${serverId}/roles/${id}`, patch)
    } catch (e: any) {
      showToast({ id: `role-update-${Date.now()}`, type: 'error', title: 'Failed to update role', body: e?.response?.data?.error })
      fetchRoles()
    }
  }

  const toggleGroup = (id: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          Permissions sit in {PERMISSION_GROUPS.length} groups. Tick the box to grant on this role.
          <span className="text-amber-500"> Administrator</span> bypasses every other check.
        </div>
        <div className="flex items-center gap-2">
          {isGlobalAdmin && (
            <div className="relative">
              <button
                onClick={openClonePicker}
                disabled={cloneBusy}
                className="px-3 py-1.5 bg-muted hover:bg-accent/40 text-foreground rounded text-xs font-semibold transition flex items-center gap-1.5 disabled:opacity-50"
                title="Copy the role structure from another server"
              >
                <Copy size={12} />
                Clone from server
              </button>
              {cloneOpen && (
                <div className="absolute right-0 top-9 z-30 w-64 max-h-72 overflow-y-auto bg-card ring-1 ring-border rounded-lg shadow-lg py-1">
                  {cloneServers.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-muted-foreground">No other servers available.</div>
                  ) : (
                    cloneServers.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => cloneFrom(s.id, s.name)}
                        disabled={cloneBusy}
                        className="w-full text-left px-3 py-2 text-xs text-foreground hover:bg-accent/30 transition disabled:opacity-50 truncate"
                      >
                        {s.name}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
          <button
            onClick={createRole}
            className="px-3 py-1.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded text-xs font-semibold transition flex items-center gap-1.5"
          >
            <Plus size={12} />
            New role
          </button>
        </div>
      </div>

      {loading && <div className="text-muted-foreground text-center py-12 text-sm">Loading…</div>}

      {!loading && roles.length === 0 && (
        <div className="text-muted-foreground text-center py-12 text-sm">
          No roles yet — click "New role" to create one. The baseline
          (View / Send / Voice / Speak / Video / Screen Share) will be
          checked by default.
        </div>
      )}

      {!loading && roles.map((role) => {
        const memberCount = role.serverMemberRoles?.length ?? 0
        return (
          <div key={role.id} className="bg-card ring-1 ring-border rounded-xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-muted/30">
              <ColorPicker
                value={role.color}
                onChange={(c) => updateRole(role.id, { color: c })}
              />
              <input
                value={role.name}
                onChange={(e) => updateRole(role.id, { name: e.target.value })}
                className="flex-1 bg-transparent text-foreground text-sm font-semibold border-b border-transparent hover:border-border focus:border-primary outline-none transition"
              />
              <span className="text-xs text-muted-foreground flex items-center gap-1" title="Members holding this role">
                <Users size={11} /> <span className="tabular-nums">{memberCount}</span>
              </span>
              <button
                onClick={() => deleteRole(role.id, role.name)}
                className="p-1 text-muted-foreground hover:text-destructive transition"
                title="Delete role"
              >
                <Trash2 size={14} />
              </button>
            </div>

            {/* Permission groups */}
            <div className="divide-y divide-border">
              {PERMISSION_GROUPS.map((g) => {
                const collapsed = collapsedGroups.has(`${role.id}:${g.id}`)
                const enabledCount = g.perms.filter((p) => hasBit(role.permissions, p.bit)).length
                return (
                  <div key={g.id}>
                    <button
                      onClick={() => toggleGroup(`${role.id}:${g.id}`)}
                      className="w-full flex items-center gap-2 px-4 py-2 hover:bg-accent/20 transition text-left"
                    >
                      {collapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                      <Shield size={11} className={g.accent} />
                      <span className="text-xs font-semibold text-foreground">{g.label}</span>
                      <span className="text-[10px] text-muted-foreground tabular-nums">
                        {enabledCount}/{g.perms.length}
                      </span>
                    </button>
                    {!collapsed && (
                      <div className="grid grid-cols-2 gap-2 px-8 pb-3">
                        {g.perms.map((p) => (
                          <label
                            key={p.key}
                            title={p.hint}
                            className="flex items-start gap-2 text-xs text-foreground cursor-pointer hover:bg-accent/20 px-2 py-1 rounded transition"
                          >
                            <input
                              type="checkbox"
                              checked={hasBit(role.permissions, p.bit)}
                              onChange={() =>
                                updateRole(role.id, { permissions: toggleBit(role.permissions, p.bit) })
                              }
                              className="mt-0.5 rounded border-border"
                            />
                            <span>
                              <span className="block">{p.label}</span>
                              {p.hint && (
                                <span className="block text-[10px] text-muted-foreground leading-tight">{p.hint}</span>
                              )}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
      {/* Safety: prevent dead-code warning if some helper imports go unused. */}
      <div className="hidden">{ALL_PERMISSIONS.length}</div>
    </div>
  )
}

function ColorPicker({
  value,
  onChange,
}: {
  value: string
  onChange: (color: string) => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-4 h-4 rounded-full ring-1 ring-border hover:scale-110 transition"
        style={{ backgroundColor: value }}
        title="Change colour"
      />
      {open && (
        <div className="absolute left-0 top-6 z-30 bg-card ring-1 ring-border rounded shadow-lg p-2 grid grid-cols-4 gap-1.5">
          {PRESET_COLORS.map((c) => (
            <button
              key={c}
              onClick={() => { onChange(c); setOpen(false) }}
              className="w-5 h-5 rounded-full ring-1 ring-border hover:scale-110 transition"
              style={{ backgroundColor: c }}
            />
          ))}
          <input
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="col-span-4 w-full h-6 rounded cursor-pointer"
          />
        </div>
      )}
    </div>
  )
}
